import React from "react";
import { PRIORITY, STATUS, relativeTime } from "@/lib/format";

export function PriorityBadge({ value, className = "" }) {
  const p = PRIORITY[value] || PRIORITY.medium;
  return (
    <span
      data-testid={`priority-badge-${value}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border ${p.border} ${p.bg} ${p.text} ${className}`}
    >
      <span className={`w-1.5 h-1.5 ${p.dot}`} />
      {p.label}
    </span>
  );
}

export function StatusBadge({ value, className = "" }) {
  const s = STATUS[value] || { label: value, cls: "bg-surface-muted text-ink-muted" };
  return (
    <span
      data-testid={`status-badge-${value}`}
      className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${s.cls} ${className}`}
    >
      {s.label}
    </span>
  );
}

export function Label({ children, className = "" }) {
  return (
    <div className={`text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted ${className}`}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6" data-testid={testId}>
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="text-sm text-ink-muted mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border bg-surface-raised">
      {Icon && <Icon className="w-8 h-8 text-ink-faint mb-3" strokeWidth={1.5} />}
      <p className="font-heading text-lg font-semibold text-ink">{title}</p>
      {hint && <p className="text-sm text-ink-muted mt-1 max-w-sm">{hint}</p>}
    </div>
  );
}

export function Stat({ label, value, hint, accent, icon: Icon, testId, onClick }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`bg-white border border-border p-4 transition-all ${onClick ? "cursor-pointer hover:-translate-y-[1px] hover:border-ink hover:shadow-sm" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-ink-faint" strokeWidth={2} />}
      </div>
      <div className={`mt-2 font-heading text-4xl font-bold tracking-tight ${accent || "text-ink"}`}>{value}</div>
      {hint && <div className="text-xs text-ink-muted mt-1">{hint}</div>}
    </div>
  );
}

export function Card({ children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`bg-white border border-border ${className}`}>
      {children}
    </div>
  );
}

export function CardHeader({ title, action, icon: Icon }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-brand" strokeWidth={2} />}
        <h3 className="font-heading text-sm font-semibold tracking-tight text-ink uppercase">{title}</h3>
      </div>
      {action}
    </div>
  );
}

export function TimeAgo({ iso }) {
  return <span className="font-mono text-xs text-ink-faint">{relativeTime(iso)}</span>;
}
